import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllowedFeatures } from '../../hooks/useFeaturePersonalization';
import {
  CSRCColumn1,
  CSRCColumn2,
  FSRCColumn1,
  FSRCColumn2,
  FSRCColumn3,
  FSRCColumn4,
  INRCColumn1,
  INRCColumn2,
  INRCColumn3,
  INRCColumn4,
  NYSERDAEnergyUseColumn1,
  NYSERDAEnergyUseColumn2,
  NYSERDAStatucColumn,
  ReportType,
  PaymentSessionColumn1,
  PaymentSessionColumn2,
  PaymentsChargerColumn1,
  PaymentsChargerColumn2,
  PaymentsUserColumn1,
  PaymentsUserColumn2,
} from '../../stores/types/report.interface';
import { ColorType, Label, LabelType } from '../_ui';

interface InputProps {
  data: ReportType;
}

// This marks the string with an asterisk appended to it in the UI
// Adding a translation key to this array will add a * to the label
const requiresReferenceMark = ['payment_collected'];

export const ReportContent = memo((data: InputProps) => {
  const { allowPayterView } = useAllowedFeatures();

  const { t } = useTranslation();
  const renderColumns = (dataArr: Array<string>) => {
    return dataArr.map((col: string) => {
      return (
        <div key={col}>
          <Label text={t(col)} type={LabelType.BODY3} color={ColorType.GREY6} className='inline' />
          {requiresReferenceMark.includes(col) && (
            <span className={`${LabelType.BODY4} text-${ColorType.GREY6} align-text-top`}>*</span>
          )}
        </div>
      );
    });
  };

  const CSRColumn2List = useMemo(() => {
    if (allowPayterView) {
      const csrColumns2 = [...CSRCColumn2];
      csrColumns2.splice(csrColumns2.indexOf('payment_collected'), 1);
      return [
        ...csrColumns2,
        'payter_total_revenue',
        'direct_revenue_collected',
        'payter_terminal_revenue_connected',
      ];
    }
    return CSRCColumn2;
  }, [allowPayterView]);

  const FSRCColumn3List = useMemo(() => {
    if (allowPayterView) {
      const fsrColumns3 = [...FSRCColumn3];
      return [...fsrColumns3, 'payment_terminal_id_if_applicable'];
    }
    return FSRCColumn3;
  }, [allowPayterView]);

  const INRCColumn4List = useMemo(() => {
    if (allowPayterView) {
      const inrColumns4 = [...INRCColumn4];
      return [...inrColumns4, 'payment_terminal_id_if_applicable'];
    }
    return INRCColumn4;
  }, [allowPayterView]);

  const paymentDisclosureMarkup = (
    <div className='text-left text-[outline-0 border-0 text-[0.75rem] leading-5 font-normal font-inter text-grey6 mt-2'>
      <span className={LabelType.BODY4}>*</span>
      <p className='inline'>{t('payment_collected_disclosure')}</p>
    </div>
  );

  return (
    <div className='flex flex-row justify-between'>
      {data.data === ReportType.CHARGER_STATS && (
        <>
          <div className='flex flex-col gap-2 w-1/2'>{renderColumns(CSRCColumn1)}</div>
          <div className='flex flex-col gap-[8px] w-1/2'>{renderColumns(CSRColumn2List)}</div>
        </>
      )}
      {data.data === ReportType.FULL_SESSION && (
        <>
          <div className='flex flex-col gap-[8px]'>{renderColumns(FSRCColumn1)}</div>
          <div className='flex flex-col gap-[8px]'>{renderColumns(FSRCColumn2)}</div>
          <div className='flex flex-col gap-[8px]'>
            {renderColumns(FSRCColumn3List)}
            {paymentDisclosureMarkup}
          </div>
          <div className='flex flex-col gap-[8px]'>{renderColumns(FSRCColumn4)}</div>
        </>
      )}
      {data.data === ReportType.INTERVALS && (
        <>
          <div className='flex flex-col gap-[8px]'>{renderColumns(INRCColumn1)}</div>
          <div className='flex flex-col gap-[8px]'>{renderColumns(INRCColumn2)}</div>
          <div className='flex flex-col gap-[8px]'>{renderColumns(INRCColumn3)}</div>
          <div className='flex flex-col gap-[8px]'>
            {renderColumns(INRCColumn4List)}
            {paymentDisclosureMarkup}
          </div>
        </>
      )}
      {data.data === ReportType.NYSERDA_STATUS_HISTORY && (
        <div className='flex flex-col gap-[8px] w-1/3'>{renderColumns(NYSERDAStatucColumn)}</div>
      )}
      {data.data === ReportType.NYSERDA_ENERGY_USE && (
        <>
          <div className='flex flex-col gap-[8px] w-1/3'>
            {renderColumns(NYSERDAEnergyUseColumn1)}
          </div>
          <div className='flex flex-col gap-[8px] w-1/3'>
            {renderColumns(NYSERDAEnergyUseColumn2)}
          </div>
        </>
      )}
      {data.data === ReportType.USERS && (
        <>
          <div className='flex flex-col gap-[8px] w-1/2'>{renderColumns(PaymentsUserColumn1)}</div>
          <div className='flex flex-col gap-[8px] w-1/2'>{renderColumns(PaymentsUserColumn2)}</div>
        </>
      )}
      {data.data === ReportType.CHARGERS && (
        <>
          <div className='flex flex-col gap-[8px] w-1/2'>
            {renderColumns(PaymentsChargerColumn1)}
          </div>
          <div className='flex flex-col gap-[8px] w-1/2'>
            {renderColumns(PaymentsChargerColumn2)}
          </div>
        </>
      )}
      {data.data === ReportType.SESSIONS && (
        <>
          <div className='flex flex-col gap-[8px] w-1/2'>
            {renderColumns(PaymentSessionColumn1)}
          </div>
          <div className='flex flex-col gap-[8px] w-1/2'>
            {renderColumns(PaymentSessionColumn2)}
          </div>
        </>
      )}
    </div>
  );
});
